/**
 * @fileoverview Song Detail Panel Component
 *
 * Left-side panel displaying selected song details, lyrics, audio player,
 * and "Copy to My Deck" functionality.
 *
 * **Features:**
 * - Song title and verb information
 * - Lyrics display (alternating chorus/verse sections)
 * - Howler.js audio player with speed controls
 * - Deck selection modal for copying
 * - Loading and error states
 * - Global playback state synchronization
 *
 * @module components/SongDetailPanel
 */

import { useState, useCallback } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import type { Doc, Id } from '../../convex/_generated/dataModel'
import { Music, Copy, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { usePostHog } from '@/hooks/usePostHog'
import { WaveformAudioPlayer } from '@/components/WaveformAudioPlayer'

interface SongDetailPanelProps {
  song: Doc<'songs'> & { audioGeneration?: Doc<'temporGenerations'> | null }
}

export function SongDetailPanel({ song }: SongDetailPanelProps) {
  const [showDeckModal, setShowDeckModal] = useState(false)
  const [selectedDeckId, setSelectedDeckId] = useState<Id<'flashcardDecks'> | null>(null)
  const [newDeckName, setNewDeckName] = useState('')
  const [copyStatus, setCopyStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const currentUser = useCurrentUser()
  const { trackEvent } = usePostHog()

  // Fetch user's decks
  const decks = useQuery(
    api.flashcards.getDecks,
    currentUser ? { userId: currentUser._id } : 'skip'
  )

  // Get audio URL directly from song's audioStorageId
  const audioUrl = useQuery(
    api.songs.getAudioUrl,
    { songId: song._id }
  )

  // Get flashcard count for this song (if user has created flashcards from it)
  const flashcardCount = useQuery(
    api.flashcards.countFlashcardsBySong,
    song.audioGeneration?._id && currentUser ? { generationId: song.audioGeneration._id } : 'skip'
  )

  // Use audio URL from direct storage
  const effectiveAudioUrl = audioUrl ?? null
  const copyToUserDeck = useMutation(api.songs.copyToUserDeck)

  // Audio player event handlers for tracking
  const handlePlay = useCallback(() => {
    trackEvent('song_played', {
      songType: 'song',
      songId: song._id,
    })
  }, [trackEvent, song._id])

  const handleCopyToDeck = async () => {
    setCopyStatus('loading')
    setErrorMessage('')

    try {
      if (!selectedDeckId && !newDeckName.trim()) {
        setErrorMessage('Please select a deck or enter a name for a new deck')
        setCopyStatus('error')
        return
      }

      await copyToUserDeck({
        songId: song._id,
        deckId: selectedDeckId || undefined,
        deckName: newDeckName.trim() || undefined,
      })

      setCopyStatus('success')
      setTimeout(() => {
        setShowDeckModal(false)
        setCopyStatus('idle')
        setNewDeckName('')
        setSelectedDeckId(null)
      }, 1500)
    } catch (error) {
      console.error('Error copying song to deck:', error)
      setErrorMessage(error instanceof Error ? error.message : 'Failed to copy song to deck')
      setCopyStatus('error')
    }
  }

  return (
    <div className="flex flex-col h-full bg-black">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-6 border-b border-gray-700">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white mb-2">{song.title}</h1>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span className="font-medium text-cyan-400">{song.targetVerb}</span>
              <span>•</span>
              <span>{song.sourceVerb}</span>
              {song.difficulty && (
                <>
                  <span>•</span>
                  <span className="capitalize">{song.difficulty}</span>
                </>
              )}
              {flashcardCount !== undefined && flashcardCount > 0 && (
                <>
                  <span>•</span>
                  <span className="text-green-500 font-medium">
                    {flashcardCount} flashcard{flashcardCount !== 1 ? 's' : ''} in your deck
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowDeckModal(true)}
            className="flex items-center gap-2 px-4 py-2 sm:py-2.5 text-sm sm:text-lg font-bold bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
          >
            <Copy className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Copy to My Deck</span>
            <span className="sm:hidden">Copy</span>
          </button>
        </div>
      </div>

      {/* Waveform Audio Player */}
      {effectiveAudioUrl && (
        <div className="flex-shrink-0 px-6 py-4">
          <WaveformAudioPlayer
            audioUrl={effectiveAudioUrl}
            audioId={song._id}
            title={`${song.title} - ${song.targetVerb}`}
            onPlay={handlePlay}
          />
        </div>
      )}

      {/* Lyrics */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <h2 className="text-lg font-semibold text-white mb-4">Lyrics</h2>
        <div className="space-y-6">
          {song.sections.map((section, index) => (
            <div
              key={index}
              className={`
                p-4 rounded-lg border-l-4
                ${
                  section.type === 'chorus'
                    ? 'bg-cyan-950 border-cyan-500'
                    : 'bg-gray-900 border-gray-600'
                }
              `}
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={`
                    text-xs font-semibold uppercase px-2 py-1 rounded
                    ${section.type === 'chorus' ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-white'}
                  `}
                >
                  {section.type}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-400 mb-1">Spanish</p>
                  <p className="text-white whitespace-pre-line">{section.spanish}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-400 mb-1">English</p>
                  <p className="text-gray-300 whitespace-pre-line">{section.english}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Deck Selection Modal */}
      {showDeckModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div className="bg-gray-900 border-2 border-gray-700 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">Copy Song to Deck</h3>
              <p className="text-sm text-gray-400 mt-1">
                Select an existing deck or create a new one
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {/* Existing Decks */}
              {decks && decks.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Select Existing Deck
                  </label>
                  <div className="space-y-2">
                    {decks?.map((deck: Doc<'flashcardDecks'>) => (
                      <button
                        key={deck._id}
                        onClick={() => {
                          setSelectedDeckId(deck._id)
                          setNewDeckName('')
                        }}
                        className={`
                          w-full text-left px-4 py-3 rounded-lg border-2 transition-all
                          ${
                            selectedDeckId === deck._id
                              ? 'bg-cyan-950 border-cyan-500 shadow-sm'
                              : 'bg-black border-gray-700 hover:border-cyan-400'
                          }
                        `}
                      >
                        <div className="font-medium text-white">{deck.name}</div>
                        {deck.description && (
                          <div className="text-sm text-gray-400 mt-0.5">{deck.description}</div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Create New Deck */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Or Create New Deck
                </label>
                <input
                  type="text"
                  value={newDeckName}
                  onChange={(e) => {
                    setNewDeckName(e.target.value)
                    setSelectedDeckId(null)
                  }}
                  placeholder="Enter deck name..."
                  className="w-full px-4 py-2 bg-black border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 placeholder-gray-500"
                />
              </div>

              {/* Error Message */}
              {copyStatus === 'error' && errorMessage && (
                <div className="mt-4 p-3 bg-red-950 border border-red-700 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-300">{errorMessage}</p>
                </div>
              )}

              {/* Success Message */}
              {copyStatus === 'success' && (
                <div className="mt-4 p-3 bg-green-950 border border-green-700 rounded-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <p className="text-sm text-green-300">
                    {song.sections.length} flashcards created successfully!
                  </p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => {
                  setShowDeckModal(false)
                  setCopyStatus('idle')
                  setNewDeckName('')
                  setSelectedDeckId(null)
                  setErrorMessage('')
                }}
                disabled={copyStatus === 'loading'}
                className="flex-1 px-4 py-2 sm:py-2.5 text-sm sm:text-lg font-bold border-2 border-gray-600 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCopyToDeck}
                disabled={copyStatus === 'loading' || copyStatus === 'success'}
                className="flex-1 px-4 py-2 sm:py-2.5 text-sm sm:text-lg font-bold bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {copyStatus === 'loading' ? (
                  <>
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    <span className="hidden sm:inline">Copying...</span>
                    <span className="sm:hidden">...</span>
                  </>
                ) : copyStatus === 'success' ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="hidden sm:inline">Copy {song.sections.length} Flashcards</span>
                    <span className="sm:hidden">Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Empty state component when no song is selected
 */
export function SongDetailPanelEmpty() {
  return (
    <div className="flex flex-col items-center justify-center h-full bg-black p-8 text-center">
      <Music className="w-24 h-24 text-gray-700 mb-6" />
      <h2 className="text-2xl font-semibold text-white mb-2">Select a Song</h2>
      <p className="text-gray-400 max-w-md">
        Choose a song from the list on the right to view lyrics, listen to audio (if available),
        and copy to your flashcard deck.
      </p>
    </div>
  )
}
